import React, { useState } from 'react';
import { FileText, User, Calendar, MapPin, CreditCard, Globe, CheckCircle, XCircle, AlertTriangle, Send, Languages } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import Container from '../common/Container';
import Card from '../common/Card';
import Button from '../common/Button';
import StatusBadge from '../common/StatusBadge';
const API_KEY = process.env.REACT_APP_API_KEY;
function ConsentForm({ translations, sessionId, userId, onComplete }) {
  const { lang, setLang } = useAppContext();
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    sex: '',
    passportNo: '',
    country: ''
  });

  const trans = translations?.[lang || 'en'] || translations?.en || {};
  const isRTL = lang === 'fa';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!consentAccepted) {
      alert(lang === 'fa' ? 'لطفاً شرایط و ضوابط را برای ادامه بپذیرید.' : 'Please accept the terms and conditions to proceed.');
      return;
    }

    if (!formData.firstName || !formData.lastName || !formData.dateOfBirth || !formData.sex || !formData.passportNo || !formData.country) {
      alert(lang === 'fa' ? 'لطفاً تمام فیلدهای اطلاعات گذرنامه را پر کنید.' : 'Please fill in all passport information fields.');
      return;
    }

    setLoading(true);

    try {
      const requestBody = {
        ...formData,
        passportNo: formData.passportNo.toUpperCase(),
        consentAccepted: true,
        timestamp: new Date().toISOString(),
        formLanguage: lang,
        session_id: sessionId
      };

      const response = await fetch('https://clankless-inflatedly-danae.ngrok-free.dev/submit_consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' ,
                    "x-api-key" : API_KEY
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        let error;
        try {
          error = JSON.parse(errorText);
        } catch {
          error = { detail: errorText };
        }
        throw new Error(error.detail || (lang === 'fa' ? 'ارسال ناموفق بود' : 'Submission failed'));
      }

      const result = await response.json();
      
      const alertMsg = lang === 'fa' 
        ? `تأیید هویت کامل شد!\n\nنام: ${result.data.full_name}\nگذرنامه: ${result.data.passport_no}\n\nتأیید هویت شما در حال پردازش است...\nزمان تقریبی تکمیل: ${result.processing.expected_completion}`
        : `Verification Complete!\n\nName: ${result.data.full_name}\nPassport: ${result.data.passport_no}\n\nYour verification is being processed...\nExpected completion: ${result.processing.expected_completion}`;
      
      alert(alertMsg);
      
      if (onComplete) onComplete(result);
      
    } catch (error) {
      alert((lang === 'fa' ? 'خطا: ' : 'Error: ') + error.message);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const confirmMsg = lang === 'fa' 
      ? 'آیا مطمئن هستید که می‌خواهید لغو کنید؟ تمام پیشرفت از دست خواهد رفت.'
      : 'Are you sure you want to cancel? All progress will be lost.';
    
    if (window.confirm(confirmMsg)) {
      window.location.href = '/';
    }
  };

  return (
    <div className="min-h-screen">
      <Container>
        <div className="pt-5 pb-12">
          {/* Header Section */}
          <Card variant="elevated" className="mb-6 p-6">
            <div className="flex items-start justify-between mb-4 flex-col">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-accent-green/20 flex items-center justify-center">
                    <FileText className="text-accent-green" size={20} />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-text-light">
                      {trans.consentTitle || (lang === 'fa' ? 'بررسی و رضایت' : 'Review & Consent')}
                    </h1>
                    <p className="text-sm text-text-gray mt-1">
                      {lang === 'fa' ? 'مرحله 4 از 4 - فرآیند تأیید هویت' : 'Step 4 of 4 - Identity Verification Process'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Step Progress Indicator */}
              <div className="flex gap-2">
                <div className="w-12 h-1 bg-accent-green rounded-full"></div>
                <div className="w-12 h-1 bg-accent-green rounded-full"></div>
                <div className="w-12 h-1 bg-accent-green rounded-full"></div>
                <div className="w-12 h-1 bg-accent-green rounded-full"></div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-accent-golden/10 border-2 border-accent-golden/30 rounded-xl p-4 flex gap-3">
              <AlertTriangle className="text-accent-golden flex-shrink-0 mt-0.5" size={20} />
              <div className="text-sm text-accent-golden">
                <p className="font-semibold mb-1">
                  {lang === 'fa' ? 'مهم:' : 'Important:'}
                </p>
                <p className="text-accent-golden/80">
                  {trans.formInstruction || (lang === 'fa' ? 'لطفاً فرم را طبق اطلاعات گذرنامه خود و فقط اطلاعات گذرنامه پر کنید' : 'Please fill up the form according to your passport info and passport info only')}
                </p>
              </div>
            </div>
          </Card>

          <form onSubmit={handleSubmit}>
            {/* Passport Information Card */}
            <Card variant="elevated" className="mb-6 p-4">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-accent-green/20 flex items-center justify-center">
                  <CreditCard className="text-accent-green" size={20} />
                </div>
                <h2 className="text-xl font-bold text-text-light">
                  {trans.passportInfo || (lang === 'fa' ? 'اطلاعات گذرنامه' : 'Passport Information')}
                </h2>
              </div>

              {/* Form Grid */}
              <div className="space-y-4">
                {/* First Row - Names */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="flex gap-1 items-end text-xs font-bold text-accent-green uppercase tracking-wider mb-2">
                      <User className="inline " size={20} />
                      {trans.firstName || (lang === 'fa' ? 'نام' : 'First Name')} <span className="text-red-400">*</span>
                    </label>
                    <input 
                      type="text" 
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      placeholder={trans.firstNamePlaceholder || (lang === 'fa' ? 'نام خود را وارد کنید' : 'Enter first name')}
                      className="w-full px-4 py-3 bg-secondary-black/50 border-2 border-accent-green/30 rounded-xl text-text-light focus:border-accent-green focus:outline-none transition-colors duration-200"
                    />
                  </div>
                  
                  <div>
                    <label className="flex gap-1 items-end text-xs font-bold text-accent-green uppercase tracking-wider mb-2">
                      <User className="inline " size={20} />
                      {trans.lastName || (lang === 'fa' ? 'نام خانوادگی' : 'Last Name')} <span className="text-red-400">*</span>
                    </label>
                    <input 
                      type="text" 
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      placeholder={trans.lastNamePlaceholder || (lang === 'fa' ? 'نام خانوادگی خود را وارد کنید' : 'Enter last name')}
                      className="w-full px-4 py-3 bg-secondary-black/50 border-2 border-accent-green/30 rounded-xl text-text-light focus:border-accent-green focus:outline-none transition-colors duration-200"
                    />
                  </div>
                </div>

                {/* Second Row - DOB & Sex */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="flex gap-1 items-end text-xs font-bold text-accent-green uppercase tracking-wider mb-2">
                      <Calendar className="inline " size={20} />
                      {trans.dateOfBirth || (lang === 'fa' ? 'تاریخ تولد' : 'Date of Birth')} <span className="text-red-400">*</span>
                    </label>
                    <input 
                      type="date" 
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-secondary-black/50 border-2 border-accent-green/30 rounded-xl text-text-light focus:border-accent-green focus:outline-none transition-colors duration-200"
                    />
                  </div>
                  
                  <div>
                    <label className="flex gap-1 items-end text-xs font-bold text-accent-green uppercase tracking-wider mb-2">
                      {trans.sex || (lang === 'fa' ? 'جنسیت' : 'Sex')} <span className="text-red-400">*</span>
                    </label>
                    <select 
                      name="sex"
                      value={formData.sex}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-secondary-black/50 border-2 border-accent-green/30 rounded-xl text-text-light focus:border-accent-green focus:outline-none transition-colors duration-200 cursor-pointer"
                    >
                      <option value="">{trans.selectGender || (lang === 'fa' ? 'انتخاب جنسیت' : 'Select Gender')}</option>
                      <option value="M">{trans.male || (lang === 'fa' ? 'مرد' : 'Male')}</option>
                      <option value="F">{trans.female || (lang === 'fa' ? 'زن' : 'Female')}</option>
                    </select>
                  </div>
                </div>

                {/* Third Row - Passport & Country */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="flex gap-1 items-end text-xs font-bold text-accent-green uppercase tracking-wider mb-2">
                      <CreditCard className="inline " size={20} />
                      {trans.passportNo || (lang === 'fa' ? 'شماره گذرنامه' : 'Passport No.')} <span className="text-red-400">*</span>
                    </label>
                    <input 
                      type="text" 
                      name="passportNo"
                      value={formData.passportNo}
                      onChange={handleInputChange}
                      required
                      placeholder={trans.passportNoPlaceholder || 'A12345678'}
                      className="w-full px-4 py-3 bg-secondary-black/50 border-2 border-accent-green/30 rounded-xl text-text-light focus:border-accent-green focus:outline-none transition-colors duration-200 uppercase font-semibold tracking-wider"
                    />
                  </div>
                  
                  <div>
                    <label className="flex gap-1 items-end text-xs font-bold text-accent-green uppercase tracking-wider mb-2">
                      <Globe className="inline " size={20} />
                      {trans.country || (lang === 'fa' ? 'کشور' : 'Country')} <span className="text-red-400">*</span>
                    </label>
                    <input 
                      type="text" 
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      required
                      placeholder={trans.countryPlaceholder || (lang === 'fa' ? 'مثال: ایران، آمریکا' : 'e.g., Iran, USA')}
                      className="w-full px-4 py-3 bg-secondary-black/50 border-2 border-accent-green/30 rounded-xl text-text-light focus:border-accent-green focus:outline-none transition-colors duration-200"
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Terms and Conditions Card */}
            <Card variant="elevated" className="mb-6 p-4">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-accent-green/20 flex items-center justify-center">
                  <FileText className="text-accent-green" size={20} />
                </div>
                <h2 className="text-xl font-bold text-text-light">
                  {trans.termsTitle || (lang === 'fa' ? 'شرایط و ضوابط' : 'Terms and Conditions')}
                </h2>
              </div>

              {/* Terms Content */}
              <div className="max-h-96 overflow-y-auto bg-secondary-black/30 border-2 border-accent-green/20 rounded-xl p-6 mb-6 space-y-4 scrollbar-thin scrollbar-thumb-accent-green scrollbar-track-transparent">
                <div>
                  <h3 className="font-bold text-accent-green mb-2">
                    {lang === 'fa' ? '۱. جمع‌آوری داده‌ها' : '1. Data Collection'}
                  </h3>
                  <p className="text-sm text-text-gray leading-relaxed text-justify">
                    {trans.dataCollectionText || (lang === 'fa' 
                      ? 'با پذیرش این شرایط و ضوابط، شما با جمع‌آوری، پردازش و ذخیره‌سازی اطلاعات شخصی و داده‌های بیومتریک خود برای اهداف تأیید هویت موافقت می‌کنید.'
                      : 'By accepting these terms and conditions, you agree to the collection, processing, and storage of your personal information and biometric data for identity verification purposes.'
                    )}
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-accent-green mb-2">
                    {lang === 'fa' ? '۲. حفاظت از داده‌ها' : '2. Data Protection'}
                  </h3>
                  <p className="text-sm text-text-gray leading-relaxed text-justify">
                    {trans.dataProtectionText || (lang === 'fa'
                      ? 'داده‌های شما مطابق با قوانین و مقررات حفاظت از داده‌های قابل اجرا پردازش خواهند شد. ما اقدامات فنی و سازمانی مناسب را برای محافظت از اطلاعات شما در برابر دسترسی، تغییر یا افشای غیرمجاز اعمال می‌کنیم.'
                      : 'Your data will be processed in accordance with applicable data protection laws and regulations. We implement appropriate technical and organizational measures to protect your information.'
                    )}
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-accent-green mb-2">
                    {lang === 'fa' ? '۳. داده‌های بیومتریک' : '3. Biometric Data'}
                  </h3>
                  <p className="text-sm text-text-gray leading-relaxed text-justify">
                    {trans.biometricDataText || (lang === 'fa'
                      ? 'داده‌های بیومتریک جمع‌آوری شده شامل داده‌های تشخیص چهره، اطلاعات گذرنامه و ویدیوی تأیید زنده‌بودن است. این داده‌ها صرفاً برای اهداف تأیید هویت استفاده خواهند شد.'
                      : 'The biometric data collected includes facial recognition data, passport information, and liveness verification video. This data will be used solely for identity verification purposes.'
                    )}
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-accent-green mb-2">
                    {lang === 'fa' ? '۴. حقوق شما' : '4. Your Rights'}
                  </h3>
                  <p className="text-sm text-text-gray leading-relaxed text-justify">
                    {trans.yourRightsText || (lang === 'fa'
                      ? 'شما حق دارید در هر زمان با تماس با تیم پشتیبانی ما، به داده‌های شخصی خود دسترسی پیدا کنید، آن‌ها را اصلاح یا حذف کنید.'
                      : 'You have the right to access, correct, or delete your personal data at any time by contacting our support team.'
                    )}
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-accent-green mb-2">
                    {lang === 'fa' ? '۵. سیاست حفظ حریم خصوصی' : '5. Privacy Policy'}
                  </h3>
                  <p className="text-sm text-text-gray leading-relaxed text-justify">
                    {trans.privacyPolicyText || (lang === 'fa'
                      ? 'برای اطلاعات دقیق‌تر درباره شیوه‌های مدیریت داده‌های ما، لطفاً به سیاست حفظ حریم خصوصی جامع ما موجود در وب‌سایت ما مراجعه کنید.'
                      : 'For more detailed information about our data handling practices, please refer to our comprehensive Privacy Policy.'
                    )}
                  </p>
                </div>
              </div>

              {/* Consent Checkbox */}
              <div className={`
                flex items-start gap-4 p-5 rounded-xl border-2 mb-6 transition-colors duration-200
                ${consentAccepted ? 'bg-accent-green/5 border-accent-green' : 'bg-secondary-black/30 border-accent-green/20'}
              `}>
                <input 
                  type="checkbox" 
                  id="consentCheckbox"
                  checked={consentAccepted}
                  onChange={(e) => setConsentAccepted(e.target.checked)}
                  className="w-6 h-6 mt-0.5 cursor-pointer accent-accent-green flex-shrink-0"
                />
                <label htmlFor="consentCheckbox" className="cursor-pointer">
                  <p className="font-bold text-accent-green mb-2">
                    {trans.consentCheckbox1 || (lang === 'fa' 
                      ? 'شرایط و ضوابط را به طور کامل خوانده و درک کرده‌ام'
                      : 'I have fully read and understood the terms and conditions'
                    )}
                  </p>
                  <p className="text-sm text-text-gray leading-relaxed text-justify">
                    {trans.consentCheckbox2 || (lang === 'fa'
                      ? 'من با جمع‌آوری، پردازش و ذخیره‌سازی داده‌های بیومتریک خود برای اهداف تأیید هویت موافقت می‌کنم.'
                      : 'I consent to the collection, processing, and storage of my biometric data for identity verification purposes.'
                    )}
                  </p>
                </label>
              </div>

              {/* Warning Box */}
              <div className="bg-red-500/10 border-2 border-red-500/30 rounded-xl p-4 flex gap-3">
                <AlertTriangle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-bold text-red-400 mb-1">
                    {trans.importantNotice || (lang === 'fa' ? 'اطلاعیه مهم' : 'Important Notice')}
                  </p>
                  <p className="text-sm text-red-300/80 leading-relaxed">
                    {trans.warningText || (lang === 'fa'
                      ? 'با پذیرش این شرایط، شما با جمع‌آوری داده‌های بیومتریک خود موافقت می‌کنید. اگر رد کنید، قادر به ادامه نخواهید بود.'
                      : 'By accepting these terms, you agree to the collection of your biometric data. If you decline, you will not be able to proceed.'
                    )}
                  </p>
                </div>
              </div>
            </Card>

            {/* Action Buttons */}
            <Card variant="elevated" className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Button 
                  type="button"
                  onClick={handleCancel}
                  variant="outline"
                  size="md"
                  fullWidth
                  className="border-red-500 text-red-400 hover:bg-red-500/10"
                >
                  <XCircle className={`inline ${isRTL ? 'ml-2' : 'mr-2'}`} size={18} />
                  {trans.declineBtn || (lang === 'fa' ? 'رد و بازگشت' : 'Decline & Go Back')}
                </Button>
                
                <Button 
                  type="submit"
                  disabled={!consentAccepted || loading}
                  variant="primary"
                  size="md"
                  fullWidth
                >
                  <CheckCircle className={`inline ${isRTL ? 'ml-2' : 'mr-2'}`} size={18} />
                  {loading 
                    ? (trans.processing || (lang === 'fa' ? 'در حال پردازش...' : 'Processing...'))
                    : (trans.acceptBtn || (lang === 'fa' ? 'پذیرش و ادامه' : 'Accept & Continue'))
                  }
                </Button>
              </div>
            </Card>
          </form>
        </div>
      </Container>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
          <Card variant="elevated" className="p-10 max-w-md mx-4 text-center">
            <div className="w-20 h-20 border-4 border-accent-green border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h3 className="text-2xl font-bold text-accent-green mb-3">
              {trans.processingConsent || (lang === 'fa' ? 'در حال پردازش رضایت' : 'Processing Consent')}
            </h3>
            <p className="text-text-gray">
              {trans.loadingText || (lang === 'fa' 
                ? 'لطفاً منتظر بمانید تا تأیید هویت شما نهایی شود...'
                : 'Please wait while we finalize your verification...'
              )}
            </p>
            <p className="text-sm text-accent-green mt-2">
              {trans.loadingSubtext || (lang === 'fa' ? 'این فقط یک لحظه طول می‌کشد' : 'This will only take a moment')}
            </p>
          </Card>
        </div>
      )}
    </div>
  );
}

export default ConsentForm;